import './veranstaltungen.scss';

/**
 * Manages intro image switching based on category filters
 */
class IntroImageManager {
    constructor(programm) {
        this.programm = programm;
        this.imageWrapper = document.querySelector('[data-intro-image]');

        if (!this.imageWrapper) {
            console.warn('Intro image wrapper not found');
            return;
        }

        this.categoryImagesDesktop = this.parseCategoryImages('desktop');
        this.categoryImagesMobile = this.parseCategoryImages('mobile');
        this.defaultImages = {
            desktop: this.imageWrapper.querySelector('[data-default-image][data-device="desktop"]'),
            mobile: this.imageWrapper.querySelector('[data-default-image][data-device="mobile"]')
        };
        this.currentActiveImage = this.getInitialImage();
        this.isMobile = this.checkIsMobile();

        this.init();
    }

    /**
     * Get initial active image based on viewport
     */
    getInitialImage() {
        const isMobile = this.checkIsMobile();
        const preferredDevice = isMobile ? 'mobile' : 'desktop';
        const fallbackDevice = isMobile ? 'desktop' : 'mobile';

        return this.defaultImages[preferredDevice]
            || this.defaultImages[fallbackDevice]
            || null;
    }

    /**
     * Check if current viewport is mobile
     */
    checkIsMobile() {
        return window.innerWidth < 768;
    }

    /**
     * Parse category images from data attribute
     */
    parseCategoryImages(device) {
        const attr = device === 'mobile' ? 'categoryImagesMobile' : 'categoryImagesDesktop';
        const data = this.imageWrapper.dataset[attr];

        if (!data) {
            console.warn(`No ${device} category images data found`);
            return {};
        }

        try {
            return JSON.parse(data);
        } catch (error) {
            console.error(`Error parsing ${device} category images:`, error);
            return {};
        }
    }

    /**
     * Initialize image manager
     */
    init() {
        console.log('IntroImageManager initialized', {
            desktopImages: Object.keys(this.categoryImagesDesktop).length,
            mobileImages: Object.keys(this.categoryImagesMobile).length,
            hasDefaultDesktop: !!this.defaultImages.desktop,
            hasDefaultMobile: !!this.defaultImages.mobile,
            isMobile: this.isMobile
        });

        const correctImage = this.getInitialImage();
        if (correctImage && correctImage !== this.currentActiveImage) {
            console.log('Correcting initial active image for viewport');
            this.switchToImage(correctImage);
        }

        window.addEventListener('resize', () => {
            const wasMobile = this.isMobile;
            this.isMobile = this.checkIsMobile();

            if (wasMobile !== this.isMobile) {
                console.log('Viewport changed, updating image');
                this.onFilterChange();
            }
        });
    }

    /**
     * Update image based on selected category
     */
    updateImage(termId) {
        if (!termId) {
            this.showDefaultImage();
            return;
        }

        const preferredDevice = this.isMobile ? 'mobile' : 'desktop';
        const fallbackDevice = this.isMobile ? 'desktop' : 'mobile';

        let targetImage = this.imageWrapper.querySelector(
            `[data-term-id="${termId}"][data-device="${preferredDevice}"]`
        );

        if (!targetImage) {
            console.log(`No ${preferredDevice} image for term ${termId}, trying ${fallbackDevice} fallback`);
            targetImage = this.imageWrapper.querySelector(
                `[data-term-id="${termId}"][data-device="${fallbackDevice}"]`
            );
        }

        if (!targetImage) {
            console.log(`No image configured for term ID: ${termId}, keeping current image`);
            return;
        }

        if (targetImage === this.currentActiveImage) {
            return;
        }

        this.switchToImage(targetImage);
    }

    /**
     * Switch to default image based on current viewport
     */
    showDefaultImage() {
        const preferredDevice = this.isMobile ? 'mobile' : 'desktop';
        const fallbackDevice = this.isMobile ? 'desktop' : 'mobile';

        const defaultImage = this.defaultImages[preferredDevice]
            || this.defaultImages[fallbackDevice];

        if (!defaultImage) {
            console.warn('No default image found');
            return;
        }

        if (this.currentActiveImage === defaultImage) {
            return;
        }

        this.switchToImage(defaultImage);
    }

    /**
     * Perform smooth image switch with crossfade
     */
    switchToImage(newImage) {
        if (this.currentActiveImage) {
            this.currentActiveImage.classList.remove('active');
        }

        newImage.classList.add('active');
        newImage.removeAttribute('style');
        this.currentActiveImage = newImage;

        console.log('Switched to image:', {
            termId: newImage.dataset.termId || 'default',
            device: newImage.dataset.device || 'desktop'
        });
    }

    /**
     * Get last selected category term ID
     */
    getLastSelectedCategory() {
        const categoryFilters = this.programm.selectedFilters['veranstaltungskategorien'];

        if (!categoryFilters || categoryFilters.length === 0) {
            return null;
        }

        return categoryFilters[categoryFilters.length - 1];
    }

    /**
     * Handle filter change
     */
    onFilterChange() {
        const lastTermId = this.getLastSelectedCategory();

        if (lastTermId) {
            this.updateImage(lastTermId);
        } else {
            this.showDefaultImage();
        }
    }
}

/**
 * Veranstaltungsprogramm JavaScript
 */
class VeranstaltungsProgramm {
    constructor() {
        this.tabs = document.querySelector('[data-ref="tabs"]');
        this.tabPanels = document.querySelector('[data-ref="tab-content"]');
        this.filter = document.querySelector('[data-ref="filter"]');
        this.activeFilters = document.querySelector('[data-ref="active-filters"]');
        this.clearAllBtn = document.querySelector('[data-action="clear-all"]');
        this.activeChips = document.querySelector('[data-ref="active-chips"]');
        this.modal = document.querySelector('[data-ref="modal"]');
        this.modalContent = document.querySelector('[data-ref="modal-content"]');
        this.searchInput = document.querySelector('[data-ref="search-input"]');
        this.searchClearBtn = document.querySelector('[data-action="clear-search"]');

        this.selectedFilters = {
            'veranstaltungskategorien': [],
            'veranstaltungs-welten': [],
            'zielgruppe': []
        };

        this.searchQuery = '';

        this.introImageManager = null;

        console.log('VeranstaltungsProgramm initialized', {
            filter: this.filter,
            searchInput: this.searchInput,
            selectedFilters: this.selectedFilters
        });

        this.init();
    }

    init() {
        this.initTabs();
        this.initFilter();
        this.initSearch();
        this.initModal();
        this.initEventListeners();
        this.initUrlHandling();
        this.checkForDeepLink();
        this.introImageManager = new IntroImageManager(this);
    }

    initUrlHandling() {
        window.addEventListener('popstate', (e) => {
            if (e.state && e.state.eventId) {
                this.openModal(e.state.eventId, e.state.eventDate || null);
            } else {
                this.closeModal();
            }
        });
    }

    checkForDeepLink() {
        const urlParams = new URLSearchParams(window.location.search);
        const eventId = urlParams.get('event');
        const eventDate = urlParams.get('date');
        if (eventId) {
            this.openModal(parseInt(eventId, 10), eventDate);
        }
    }

    updateUrl(eventId, eventDate = null) {
        const url = new URL(window.location);
        url.searchParams.set('event', eventId);
        if (eventDate) {
            url.searchParams.set('date', eventDate);
        } else {
            url.searchParams.delete('date');
        }
        window.history.pushState({eventId, eventDate}, '', url);
    }

    clearUrl() {
        const url = new URL(window.location);
        url.searchParams.delete('event');
        url.searchParams.delete('date');
        window.history.pushState({}, '', url);
    }

    getEventUrl(eventId) {
        const url = new URL(window.location.origin + window.location.pathname);
        url.searchParams.set('event', eventId);
        return url.toString();
    }

    initTabs() {
        if (!this.tabs) {
            console.warn('Tabs element not found');
            return;
        }

        const tabButtons = this.tabs.querySelectorAll('[role="tab"]');

        tabButtons.forEach(tab => {
            tab.addEventListener('click', (e) => {
                this.switchTab(e.target);
            });

            tab.addEventListener('keydown', (e) => {
                this.handleTabKeydown(e, tabButtons);
            });
        });
    }

    switchTab(clickedTab) {
        const allTabs = this.tabs.querySelectorAll('[role="tab"]');
        const allPanels = this.tabPanels.querySelectorAll('[role="tabpanel"]');

        allTabs.forEach(tab => {
            tab.classList.remove('active');
            tab.setAttribute('aria-selected', 'false');
            tab.setAttribute('tabindex', '-1');
        });

        allPanels.forEach(panel => {
            panel.classList.remove('active');
            panel.setAttribute('hidden', '');
        });

        clickedTab.classList.add('active');
        clickedTab.setAttribute('aria-selected', 'true');
        clickedTab.setAttribute('tabindex', '0');

        const targetPanel = document.getElementById(clickedTab.getAttribute('aria-controls'));
        if (targetPanel) {
            targetPanel.classList.add('active');
            targetPanel.removeAttribute('hidden');
        }
    }

    handleTabKeydown(e, tabButtons) {
        const currentIndex = Array.from(tabButtons).indexOf(e.target);

        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
            e.preventDefault();
            let nextIndex = e.key === 'ArrowRight' ? currentIndex + 1 : currentIndex - 1;

            if (nextIndex >= tabButtons.length) nextIndex = 0;
            if (nextIndex < 0) nextIndex = tabButtons.length - 1;

            this.switchTab(tabButtons[nextIndex]);
            tabButtons[nextIndex].focus();
        }

        if (e.key === 'Home') {
            e.preventDefault();
            this.switchTab(tabButtons[0]);
            tabButtons[0].focus();
        }

        if (e.key === 'End') {
            e.preventDefault();
            const lastTab = tabButtons[tabButtons.length - 1];
            this.switchTab(lastTab);
            lastTab.focus();
        }
    }

    initFilter() {
        if (!this.filter) {
            console.warn('Filter element not found');
            return;
        }

        const filterGroups = this.filter.querySelectorAll('[name="QuartalsprogrammFilterGroup"]');

        filterGroups.forEach(group => {
            const toggle = group.querySelector('.toggle');
            const content = group.querySelector('.content');
            const checkboxes = group.querySelectorAll('input[type="checkbox"]');
            const counter = group.querySelector('[data-counter]');

            if (toggle && content) {
                toggle.addEventListener('click', () => {
                    const isExpanded = toggle.getAttribute('aria-expanded') === 'true';

                    toggle.setAttribute('aria-expanded', !isExpanded);

                    if (isExpanded) {
                        content.setAttribute('hidden', '');
                    } else {
                        content.removeAttribute('hidden');
                    }
                });
            }

            checkboxes.forEach(checkbox => {
                checkbox.addEventListener('change', (e) => {
                    this.handleFilterChange(e.target);
                    this.updateFilterCounter(group, counter);
                });
            });
        });

        if (this.clearAllBtn) {
            this.clearAllBtn.addEventListener('click', () => {
                this.clearAllFilters();
            });
        }
    }

    handleFilterChange(checkbox) {
        const taxonomy = checkbox.dataset.taxonomy;
        const termId = checkbox.value;

        console.log('Filter changed:', {
            taxonomy,
            termId,
            checked: checkbox.checked
        });

        if (checkbox.checked) {
            if (!this.selectedFilters[taxonomy].includes(termId)) {
                this.selectedFilters[taxonomy].push(termId);
            }
        } else {
            this.selectedFilters[taxonomy] = this.selectedFilters[taxonomy].filter(id => id !== termId);
        }

        if (taxonomy === 'veranstaltungskategorien') {
            this.introImageManager?.onFilterChange();
        }

        this.updateActiveFilters();
        this.applyFilters();
    }

    updateFilterCounter(group, counter) {
        const checkedCount = group.querySelectorAll('input[type="checkbox"]:checked').length;

        if (checkedCount > 0) {
            counter.textContent = checkedCount;
            counter.removeAttribute('hidden');
        } else {
            counter.setAttribute('hidden', '');
        }
    }

    updateActiveFilters() {
        const hasActiveFilters = Object.values(this.selectedFilters).some(arr => arr.length > 0);

        if (hasActiveFilters) {
            this.activeFilters.removeAttribute('hidden');
            this.clearAllBtn.removeAttribute('hidden');
            this.renderActiveChips();
        } else {
            this.activeFilters.setAttribute('hidden', '');
            this.clearAllBtn.setAttribute('hidden', '');
        }
    }

    renderActiveChips() {
        this.activeChips.innerHTML = '';

        Object.entries(this.selectedFilters).forEach(([taxonomy, termIds]) => {
            termIds.forEach(termId => {
                const checkbox = this.filter.querySelector(
                    `input[data-taxonomy="${taxonomy}"][value="${termId}"]`
                );

                if (checkbox) {
                    const label = checkbox.parentElement.querySelector('span');
                    const chip = this.createActiveChip(label.textContent, taxonomy, termId);
                    this.activeChips.appendChild(chip);
                }
            });
        });
    }

    createActiveChip(text, taxonomy, termId) {
        const chip = document.createElement('span');
        chip.className = 'chip chip--blue';
        chip.innerHTML = `
            ${text}
            <button type="button" class="chip__remove" aria-label="${text} entfernen">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M7.64062 6.36719L14 0.00878906L15.2734 1.28223L8.91406 7.64062L15.2734 14L14 15.2734L7.64062 8.91406L1.27344 15.2822L0 14.0088L6.36719 7.64062L0 1.27344L1.27344 0L7.64062 6.36719Z" fill="currentColor"/>
                </svg>
            </button>
        `;

        chip.querySelector('.chip__remove').addEventListener('click', () => {
            this.removeFilter(taxonomy, termId);
        });

        return chip;
    }

    removeFilter(taxonomy, termId) {
        const checkbox = this.filter.querySelector(
            `input[data-taxonomy="${taxonomy}"][value="${termId}"]`
        );

        if (checkbox) {
            checkbox.checked = false;
            this.handleFilterChange(checkbox);

            const group = checkbox.closest('[name="QuartalsprogrammFilterGroup"]');
            const counter = group?.querySelector('[data-counter]');
            if (group && counter) {
                this.updateFilterCounter(group, counter);
            }
        }
    }

    clearAllFilters() {
        const allCheckboxes = this.filter.querySelectorAll('input[type="checkbox"]:checked');

        allCheckboxes.forEach(checkbox => {
            checkbox.checked = false;
        });

        Object.keys(this.selectedFilters).forEach(key => {
            this.selectedFilters[key] = [];
        });

        const allCounters = this.filter.querySelectorAll('[data-counter]');
        allCounters.forEach(counter => {
            counter.setAttribute('hidden', '');
        });

        this.activeFilters.setAttribute('hidden', '');
        this.clearAllBtn.setAttribute('hidden', '');
        this.activeChips.innerHTML = '';

        this.introImageManager?.onFilterChange();
        this.applyFilters();
    }

    initSearch() {
        if (!this.searchInput) {
            console.warn('Search input not found');
            return;
        }

        this.searchInput.addEventListener('input', (e) => {
            this.searchQuery = e.target.value.toLowerCase();
            this.applyFilters();
            this.updateSearchClearButton();
        });

        if (this.searchClearBtn) {
            this.searchClearBtn.addEventListener('click', () => {
                this.searchInput.value = '';
                this.searchQuery = '';
                this.applyFilters();
                this.updateSearchClearButton();
                this.searchInput.focus();
            });
        }
    }

    updateSearchClearButton() {
        if (!this.searchClearBtn) return;

        if (this.searchQuery.length > 0) {
            this.searchClearBtn.style.display = 'flex';
        } else {
            this.searchClearBtn.style.display = 'none';
        }
    }

    applyFilters() {
        const hasActiveFilters = Object.values(this.selectedFilters).some(arr => arr.length > 0);
        const hasSearchQuery = this.searchQuery.length > 0;
        const allItems = document.querySelectorAll('.veranstaltung-item');

        console.log('Applying filters:', {
            filters: this.selectedFilters,
            search: this.searchQuery,
            hasActiveFilters,
            hasSearchQuery
        });

        if (!hasActiveFilters && !hasSearchQuery) {
            allItems.forEach(item => {
                item.removeAttribute('hidden');
            });
            const noResultMessages = document.querySelectorAll('[data-ref="no-filter-results"]');
            noResultMessages.forEach(msg => msg.setAttribute('hidden', ''));
            return;
        }

        allItems.forEach(item => {
            const event = item.querySelector('.veranstaltung');
            if (!event) return;

            const matchesFilter = this.itemMatchesFilters(event);
            const matchesSearch = this.itemMatchesSearch(event);

            if (matchesFilter && matchesSearch) {
                item.removeAttribute('hidden');
            } else {
                item.setAttribute('hidden', '');
            }
        });

        const panels = document.querySelectorAll('.tab-content');
        panels.forEach(panel => {
            const list = panel.querySelector('.veranstaltungen-list');
            const msg = panel.querySelector('[data-ref="no-filter-results"]');
            if (!list || !msg) return;

            const visibleItems = list.querySelectorAll('.veranstaltung-item:not([hidden])');
            if (visibleItems.length === 0) {
                msg.removeAttribute('hidden');
            } else {
                msg.setAttribute('hidden', '');
            }
        });
    }

    /**
     * Checks if an event matches the selected filters.
     * Supports hierarchical taxonomy matching - if a parent category is selected,
     * events with child categories will also match.
     */
    itemMatchesFilters(eventElement) {
        const hasActiveFilters = Object.values(this.selectedFilters).some(arr => arr.length > 0);

        if (!hasActiveFilters) {
            return true;
        }

        const eventChips = eventElement.querySelectorAll('.chip[data-taxonomy]');
        const eventTerms = {};

        eventChips.forEach(chip => {
            const taxonomy = chip.dataset.taxonomy;
            const termId = chip.dataset.term;
            const parentTerms = chip.dataset.parentTerms || '';

            if (!eventTerms[taxonomy]) {
                eventTerms[taxonomy] = {
                    terms: [],
                    parentTerms: []
                };
            }

            eventTerms[taxonomy].terms.push(termId);

            if (parentTerms) {
                const parentIds = parentTerms.split(',').filter(id => id.trim() !== '');
                eventTerms[taxonomy].parentTerms.push(...parentIds);
            }
        });

        const matches = Object.entries(this.selectedFilters).every(([taxonomy, selectedTermIds]) => {
            if (selectedTermIds.length === 0) {
                return true;
            }

            const taxonomyData = eventTerms[taxonomy] || { terms: [], parentTerms: [] };
            const allMatchableTerms = [...taxonomyData.terms, ...taxonomyData.parentTerms];

            return selectedTermIds.some(termId => allMatchableTerms.includes(termId));
        });

        return matches;
    }

    itemMatchesSearch(eventElement) {
        if (this.searchQuery.length === 0) {
            return true;
        }

        const searchContent = eventElement.dataset.searchContent || '';
        return searchContent.includes(this.searchQuery);
    }

    initModal() {
        if (!this.modal) {
            console.warn('Modal not found');
            return;
        }

        document.addEventListener('click', (e) => {
            if (e.target.matches('.veranstaltung__more') || e.target.closest('.veranstaltung__more')) {
                e.preventDefault();
                const moreBtn = e.target.closest('.veranstaltung__more');
                const eventId = parseInt(moreBtn.dataset.eventId, 10);
                const eventDate = moreBtn.dataset.eventDate || null;
                this.openModal(eventId, eventDate);
            }
        });

        this.modal.addEventListener('click', (e) => {
            if (e.target.matches('[data-action="close-modal"]') ||
                e.target.closest('[data-action="close-modal"]')) {
                this.closeModal();
            }
        });

        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && !this.modal.hasAttribute('hidden')) {
                this.closeModal();
            }
        });
    }

    initEventListeners() {
        document.addEventListener('click', (e) => {
            if (e.target.matches('[data-action="toggle-share"]') ||
                e.target.closest('[data-action="toggle-share"]')) {
                e.stopPropagation();
                if (navigator.share && this.currentEventData) {
                    this.shareEvent(this.currentEventData);
                    return;
                }

                const shareBtn = e.target.closest('[data-action="toggle-share"]');
                const shareWrapper = shareBtn.closest('[data-ref="share-dropdown"]');
                const dropdown = shareWrapper.querySelector('.share-dropdown');

                if (dropdown) {
                    const isHidden = dropdown.hasAttribute('hidden');
                    dropdown.toggleAttribute('hidden', !isHidden);

                    if (!isHidden) {
                        this.adjustDropdownPosition(dropdown);
                    }
                }
            } else {
                const openDropdowns = document.querySelectorAll('.share-dropdown:not([hidden])');
                openDropdowns.forEach(dropdown => dropdown.setAttribute('hidden', ''));
            }
        });
    }

    openModal(eventId, eventDate = null) {
        console.log('openModal called with:', { eventId, eventDate });

        const eventElement = document.querySelector(`.veranstaltung[data-event-id="${eventId}"]`);

        if (!eventElement) {
            console.error('Event not found:', eventId);
            return;
        }

        const eventDataStr = eventElement.dataset.eventModal;
        let eventData;

        try {
            eventData = JSON.parse(eventDataStr);
        } catch (error) {
            console.error('Error parsing event data:', error);
            return;
        }

        this.populateModal(eventData, eventDate);
        this.modal.removeAttribute('hidden');
        document.body.style.overflow = 'hidden';
        this.updateUrl(eventId, eventDate);

        this.modalContent.focus();
    }

    populateModal(eventData, specificDate = null) {
        const template = document.getElementById('modal-content-template');
        if (!template) {
            console.error('Modal template not found');
            return;
        }

        const clone = template.content.cloneNode(true);
        this.currentEventData = eventData;

        let displayDates = eventData.dates;
        if (specificDate && eventData.dates && eventData.dates.length > 0) {
            console.log('Filtering for specificDate:', specificDate);
            console.log('Available dates:', eventData.dates.map(d => d.date));

            const foundDate = eventData.dates.find(d => {
                const match = d.date === specificDate ||
                    d.date === specificDate.trim() ||
                    new Date(d.date).toISOString().split('T')[0] === specificDate;
                if (match) {
                    console.log('Found matching date:', d);
                }
                return match;
            });

            if (foundDate) {
                displayDates = [foundDate];
                console.log('Using filtered date:', foundDate);
            } else {
                console.warn('No matching date found for:', specificDate);
            }
        }

        this.setElementContent(clone, '[data-field="title"]', eventData.title);
        this.setElementContent(clone, '[data-field="content"]', eventData.content, true);

        const actionsContainer = clone.querySelector('[data-ref="modal-actions"]');
        if (actionsContainer && displayDates && displayDates.length > 0) {
            const calendarWrapper = document.createElement('div');
            calendarWrapper.className = 'modal-event__calendar';

            const calendarBtn = document.createElement('button');
            calendarBtn.type = 'button';
            calendarBtn.className = 'button modal-button';
            calendarBtn.innerHTML = `
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <rect x="2.5" y="4.16666" width="15" height="13.3333" rx="1.66667" stroke="currentColor" stroke-width="1.5"/>
                  <path d="M2.5 7.5H17.5M6.66666 2.5V5.83333M13.3333 2.5V5.83333" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                </svg>
                zum Kalender hinzufügen
            `;

            if (displayDates.length === 1) {
                calendarBtn.addEventListener('click', () => {
                    this.downloadIcal(eventData, 0, displayDates[0]);
                });
                calendarWrapper.appendChild(calendarBtn);
            } else {
                calendarBtn.setAttribute('data-action', 'toggle-calendar');

                const calendarDropdown = document.createElement('div');
                calendarDropdown.className = 'calendar-dropdown';
                calendarDropdown.setAttribute('hidden', '');
                calendarDropdown.setAttribute('data-ref', 'calendar-dropdown');

                displayDates.forEach((date, index) => {
                    const dateBtn = document.createElement('button');
                    dateBtn.type = 'button';
                    dateBtn.className = 'calendar-dropdown__item';
                    dateBtn.textContent = date.formatted;

                    if (date.is_canceled) {
                        dateBtn.classList.add('calendar-dropdown__item--canceled');
                        dateBtn.disabled = true;
                    } else {
                        dateBtn.addEventListener('click', () => {
                            this.downloadIcal(eventData, index, date);
                            calendarDropdown.setAttribute('hidden', '');
                        });
                    }

                    calendarDropdown.appendChild(dateBtn);
                });

                calendarBtn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    const isHidden = calendarDropdown.hasAttribute('hidden');
                    calendarDropdown.toggleAttribute('hidden', !isHidden);

                    if (!isHidden) {
                        this.adjustDropdownPosition(calendarDropdown);
                    }
                });

                calendarWrapper.appendChild(calendarBtn);
                calendarWrapper.appendChild(calendarDropdown);
            }

            actionsContainer.appendChild(calendarWrapper);

            const shareWrapper = document.createElement('div');
            shareWrapper.className = 'modal-event__share';
            shareWrapper.setAttribute('data-ref', 'share-dropdown');

            const shareBtn = document.createElement('button');
            shareBtn.type = 'button';
            shareBtn.className = 'button modal-button';
            shareBtn.innerHTML = `
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <circle cx="4.99984" cy="9.99999" r="2.58333" stroke="currentColor" stroke-width="1.5"/>
                  <circle cx="14.1668" cy="15.8333" r="2.58333" stroke="currentColor" stroke-width="1.5"/>
                  <circle cx="14.1668" cy="4.16668" r="2.58333" stroke="currentColor" stroke-width="1.5"/>
                  <path d="M12.0326 5.41667L7.19692 8.56822M7.19692 11.4318L12.0326 14.5833" stroke="currentColor" stroke-width="1.66667"/>
                </svg>
                teilen
            `;
            shareBtn.setAttribute('data-action', 'toggle-share');

            const shareDropdown = document.createElement('div');
            shareDropdown.className = 'share-dropdown';
            shareDropdown.setAttribute('hidden', '');

            const shareDropdownInner = document.createElement('div');
            shareDropdownInner.setAttribute('data-ref', 'share-options');

            const eventUrl = this.getEventUrl(eventData.id);

            const whatsappItem = document.createElement('a');
            const shareText = `${eventData.title} – ${eventUrl}`;
            whatsappItem.href = `https://api.whatsapp.com/send?text=${encodeURIComponent(shareText)}`;
            whatsappItem.target = '_blank';
            whatsappItem.rel = 'noopener noreferrer';
            whatsappItem.className = 'share-dropdown__item';
            whatsappItem.innerHTML = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M7.25361 18.4944L7.97834 18.917C9.18909 19.623 10.5651 20 12.001 20C16.4193 20 20.001 16.4183 20.001 12C20.001 7.58172 16.4193 4 12.001 4C7.5827 4 4.00098 7.58172 4.00098 12C4.00098 13.4363 4.37821 14.8128 5.08466 16.0238L5.50704 16.7478L4.85355 19.1494L7.25361 18.4944ZM2.00516 22L3.35712 17.0315C2.49494 15.5536 2.00098 13.8345 2.00098 12C2.00098 6.47715 6.47813 2 12.001 2C17.5238 2 22.001 6.47715 22.001 12C22.001 17.5228 17.5238 22 12.001 22C10.1671 22 8.44851 21.5064 6.97086 20.6447L2.00516 22ZM8.39232 7.30833C8.5262 7.29892 8.66053 7.29748 8.79459 7.30402C8.84875 7.30758 8.90265 7.31384 8.95659 7.32007C9.11585 7.33846 9.29098 7.43545 9.34986 7.56894C9.64818 8.24536 9.93764 8.92565 10.2182 9.60963C10.2801 9.76062 10.2428 9.95633 10.125 10.1457C10.0652 10.2428 9.97128 10.379 9.86248 10.5183C9.74939 10.663 9.50599 10.9291 9.50599 10.9291C9.50599 10.9291 9.40738 11.0473 9.44455 11.1944C9.45903 11.25 9.50521 11.331 9.54708 11.3991C9.57027 11.4368 9.5918 11.4705 9.60577 11.4938C9.86169 11.9211 10.2057 12.3543 10.6259 12.7616C10.7463 12.8783 10.8631 12.9974 10.9887 13.108C11.457 13.5209 11.9868 13.8583 12.559 14.1082L12.5641 14.1105C12.6486 14.1469 12.692 14.1668 12.8157 14.2193C12.8781 14.2457 12.9419 14.2685 13.0074 14.2858C13.0311 14.292 13.0554 14.2955 13.0798 14.2972C13.2415 14.3069 13.335 14.2032 13.3749 14.1555C14.0984 13.279 14.1646 13.2218 14.1696 13.2222V13.2238C14.2647 13.1236 14.4142 13.0888 14.5476 13.097C14.6085 13.1007 14.6691 13.1124 14.7245 13.1377C15.2563 13.3803 16.1258 13.7587 16.1258 13.7587L16.7073 14.0201C16.8047 14.0671 16.8936 14.1778 16.8979 14.2854C16.9005 14.3523 16.9077 14.4603 16.8838 14.6579C16.8525 14.9166 16.7738 15.2281 16.6956 15.3913C16.6406 15.5058 16.5694 15.6074 16.4866 15.6934C16.3743 15.81 16.2909 15.8808 16.1559 15.9814C16.0737 16.0426 16.0311 16.0714 16.0311 16.0714C15.8922 16.159 15.8139 16.2028 15.6484 16.2909C15.391 16.428 15.1066 16.5068 14.8153 16.5218C14.6296 16.5313 14.4444 16.5447 14.2589 16.5347C14.2507 16.5342 13.6907 16.4482 13.6907 16.4482C12.2688 16.0742 10.9538 15.3736 9.85034 14.402C9.62473 14.2034 9.4155 13.9885 9.20194 13.7759C8.31288 12.8908 7.63982 11.9364 7.23169 11.0336C7.03043 10.5884 6.90299 10.1116 6.90098 9.62098C6.89729 9.01405 7.09599 8.4232 7.46569 7.94186C7.53857 7.84697 7.60774 7.74855 7.72709 7.63586C7.85348 7.51651 7.93392 7.45244 8.02057 7.40811C8.13607 7.34902 8.26293 7.31742 8.39232 7.30833Z"></path></svg>
                <span>Per WhatsApp teilen</span>
            `;
            shareDropdownInner.appendChild(whatsappItem);

            const copyItem = document.createElement('button');
            copyItem.type = 'button';
            copyItem.className = 'share-dropdown__item';
            copyItem.innerHTML = `
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                </svg>
                <span>Link kopieren</span>
            `;
            copyItem.addEventListener('click', () => {
                this.copyEventLink(eventData);
                shareDropdown.setAttribute('hidden', '');
            });
            shareDropdownInner.appendChild(copyItem);

            const emailItem = document.createElement('a');
            emailItem.href = `mailto:?subject=${encodeURIComponent(eventData.title)}&body=${encodeURIComponent(eventUrl)}`;
            emailItem.className = 'share-dropdown__item';
            emailItem.innerHTML = `
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <polyline points="22,6 12,13 2,6"/>
                </svg>
                <span>Per E-Mail teilen</span>
            `;
            shareDropdownInner.appendChild(emailItem);

            shareDropdown.appendChild(shareDropdownInner);
            shareWrapper.appendChild(shareBtn);
            shareWrapper.appendChild(shareDropdown);
            actionsContainer.appendChild(shareWrapper);
        }

        const badgesContainer = clone.querySelector('[data-ref="modal-badges"]');
        if (badgesContainer && eventData.taxonomies && eventData.taxonomies.length > 0) {
            eventData.taxonomies.forEach(term => {
                const chip = document.createElement('span');
                chip.className = 'chip chip--grey';
                chip.textContent = term.name;
                badgesContainer.appendChild(chip);
            });
        } else if (badgesContainer) {
            badgesContainer.remove();
        }

        if (eventData.person) {
            this.setElementContent(clone, '[data-field="person"]', eventData.person);
        } else {
            this.removeElement(clone, '[data-ref="modal-person"]');
        }

        if (eventData.organizer) {
            this.setElementContent(clone, '[data-field="organizer"]', eventData.organizer);
        } else {
            this.removeElement(clone, '[data-ref="modal-organizer"]');
        }

        if (eventData.price) {
            this.setElementContent(clone, '[data-field="price"]', eventData.price);
        } else {
            this.removeElement(clone, '[data-ref="modal-price"]');
        }

        if (eventData.info) {
            this.setElementContent(clone, '[data-field="info"]', eventData.info, true);
        } else {
            this.removeElement(clone, '[data-ref="modal-info"]');
        }

        if (eventData.for_who) {
            this.setElementContent(clone, '[data-field="for-who"]', eventData.for_who, true);
        } else {
            this.removeElement(clone, '[data-ref="modal-for-who"]');
        }

        const allCanceled = displayDates && displayDates.length > 0 &&
            displayDates.every(d => d.is_canceled);

        const headerBadgesContainer = clone.querySelector('[data-ref="modal-header-badges"]');
        if (headerBadgesContainer) {
            if (allCanceled) {
                const chip = document.createElement('span');
                chip.className = 'chip chip--black';
                chip.textContent = 'Fällt aus';
                headerBadgesContainer.appendChild(chip);
            } else if (eventData.registered_only) {
                const chip = document.createElement('span');
                chip.className = 'chip chip--blue';
                chip.textContent = 'Nur mit Anmeldung';
                headerBadgesContainer.appendChild(chip);
            } else if (eventData.has_multiple_dates && !specificDate) {
                const chip = document.createElement('span');
                chip.className = 'chip chip--blue-dark';
                chip.textContent = 'Mehrere Termine';
                headerBadgesContainer.appendChild(chip);
            }
        }

        if (displayDates && displayDates.length > 0) {
            let dateRangeText = '';

            if (displayDates.length === 1) {
                const singleDate = displayDates[0];
                dateRangeText = singleDate.formatted;

                if (singleDate.end_formatted) {
                    dateRangeText += ' – ' + singleDate.end_formatted;
                }
            } else {
                const firstDate = displayDates[0];
                const lastDate = displayDates[displayDates.length - 1];

                dateRangeText = firstDate.formatted;

                if (lastDate.end_formatted) {
                    dateRangeText += ' – ' + lastDate.end_formatted;
                } else {
                    dateRangeText += ' – ' + lastDate.formatted;
                }
            }

            this.setElementContent(clone, '[data-ref="date-range"]', dateRangeText);

            const allTimes = [];
            displayDates.forEach(date => {
                if (date.time && date.time.trim() !== '') {
                    allTimes.push(date.time);
                }
                if (date.end_time && date.end_time.trim() !== '') {
                    allTimes.push(date.end_time);
                }
            });

            const uniqueTimes = [...new Set(allTimes)];
            if (uniqueTimes.length > 0) {
                this.setElementContent(clone, '[data-ref="times"]', uniqueTimes.join(', '));
            } else {
                this.removeElement(clone, '[data-ref="times"]');
            }

            const allDatesList = clone.querySelector('[data-ref="all-dates"]');
            if (allDatesList) {
                if (eventData.dates && eventData.dates.length > 1) {
                    const dateStrings = eventData.dates.map(date => {
                        let formatted = date.date_only || date.formatted.replace(/^[A-Za-z]{2}\.\s/, '');

                        if (date.end_date_only) {
                            formatted += ' – ' + date.end_date_only;
                        }

                        if (date.is_canceled) {
                            return `<span class="date--canceled">${formatted}</span>`;
                        }
                        return formatted;
                    });
                    allDatesList.innerHTML = '<strong>Weitere Termine</strong><br>' +  dateStrings.join(', ');
                } else {
                    allDatesList.remove();
                }
            }
        }

        const registrationContainer = clone.querySelector('[data-ref="modal-registration"]');
        if (registrationContainer && !allCanceled && eventData.registered_only) {
            if (eventData.show_form) {
                const registerBtn = document.createElement('button');
                registerBtn.type = 'button';
                registerBtn.className = 'button button--primary';
                registerBtn.textContent = 'Jetzt anmelden';
                registerBtn.dataset.action = 'show-form';
                registrationContainer.appendChild(registerBtn);

                registerBtn.addEventListener('click', () => {
                    this.showRegistrationForm(eventData, displayDates[0]);
                });
            } else if (eventData.external_link) {
                const registerLink = document.createElement('a');
                let href = eventData.external_link.trim();
                let isExternalLink = true;

                const isEmail = href.includes('@') && !href.includes('://') && !href.startsWith('mailto:');
                const isPhone = !href.includes('://') && !href.startsWith('tel:') && /^[\d\s\-+()\/]+$/.test(href);

                if (isEmail) {
                    href = 'mailto:' + href;
                    isExternalLink = false;
                } else if (isPhone) {
                    href = 'tel:' + href.replace(/[\s\-()\/]/g, '');
                    isExternalLink = false;
                }

                registerLink.href = href;
                registerLink.className = 'button button--primary';
                registerLink.textContent = 'Jetzt anmelden';

                if (isExternalLink) {
                    registerLink.target = '_blank';
                    registerLink.rel = 'noopener noreferrer';
                }

                registrationContainer.appendChild(registerLink);
            }
        } else if (registrationContainer) {
            registrationContainer.remove();
        }

        const paymentInfo = clone.querySelector('[data-ref="modal-payment"]');
        const hasPriceInfo = eventData.price_info && String(eventData.price_info).trim().length > 0;

        if (paymentInfo && !allCanceled && hasPriceInfo) {
            paymentInfo.innerHTML = '';
            const strong = document.createElement('strong');
            strong.textContent = String(eventData.price_info).trim();
            paymentInfo.appendChild(strong);
        } else if (paymentInfo) {
            paymentInfo.remove();
        }

        this.modalContent.innerHTML = '';
        this.modalContent.appendChild(clone);
    }

    /**
     * Shows registration form by moving (not cloning) the Ninja Form into the modal.
     * This preserves all JavaScript initializations like Flatpickr.
     */
    showRegistrationForm(eventData, specificDate = null) {
        const formContainer = document.querySelector('[data-ref="modal-form"]');
        const registrationBtn = document.querySelector('[data-action="show-form"]');
        const sourceContainer = document.querySelector('#ninja-form-source');

        if (!formContainer || !sourceContainer) return;

        if (registrationBtn) {
            registrationBtn.style.display = 'none';
        }

        const sourceForm = sourceContainer.querySelector('.nf-form-cont');
        if (!sourceForm) {
            console.error('Ninja Form not found');
            return;
        }

        // Form verschieben (nicht klonen!) - so bleiben alle JS-Initialisierungen erhalten
        formContainer.appendChild(sourceForm);
        formContainer.removeAttribute('hidden');

        this.populateFormFields(eventData, specificDate);
    }

    /**
     * Populates form fields with event data.
     */
    populateFormFields(eventData, specificDate = null) {
        let attempts = 0;
        const maxAttempts = 10;

        const fillInterval = setInterval(() => {
            attempts++;

            const formContainer = document.querySelector('[data-ref="modal-form"]');
            if (!formContainer) {
                if (attempts >= maxAttempts) clearInterval(fillInterval);
                return;
            }

            // Programm-Feld (nf-field-232)
            const programField = formContainer.querySelector('#nf-field-232');
            if (programField) {
                programField.value = eventData.title;
                programField.dispatchEvent(new Event('change', {bubbles: true}));
                programField.dispatchEvent(new Event('input', {bubbles: true}));
            }

            // Datum-Feld (nur Datum im Format dd.mm.yyyy)
            if (specificDate && specificDate.date_only) {
                const dateValue = specificDate.date_only;

                const dateInput = formContainer.querySelector('#nf-field-238-wrap input[type="text"]');
                const hiddenDateField = formContainer.querySelector('#nf-field-238');

                if (dateInput) {
                    dateInput.value = dateValue;
                    dateInput.dispatchEvent(new Event('change', {bubbles: true}));
                }
                if (hiddenDateField) {
                    hiddenDateField.value = dateValue;
                    hiddenDateField.dispatchEvent(new Event('change', {bubbles: true}));
                }
            }

            if (programField || attempts >= maxAttempts) {
                clearInterval(fillInterval);
            }
        }, 200);
    }

    setElementContent(container, selector, content, isHTML = false) {
        const element = container.querySelector(selector);
        if (element) {
            if (isHTML) {
                element.innerHTML = content;
            } else {
                element.textContent = content;
            }
        }
    }

    removeElement(container, selector) {
        const element = container.querySelector(selector);
        if (element) {
            element.remove();
        }
    }

    /**
     * Closes modal and moves the Ninja Form back to its source container.
     */
    closeModal() {
        // Form zurück in den Source-Container verschieben
        const formContainer = document.querySelector('[data-ref="modal-form"]');
        const sourceContainer = document.querySelector('#ninja-form-source');
        const form = formContainer?.querySelector('.nf-form-cont');

        if (form && sourceContainer) {
            sourceContainer.appendChild(form);
            formContainer.innerHTML = '';
            formContainer.setAttribute('hidden', '');
        }

        this.modal.setAttribute('hidden', '');
        document.body.style.overflow = '';
        this.clearUrl();
    }

    downloadIcal(eventData, dateIndex, dateObj = null) {
        const date = dateObj || eventData.dates[dateIndex];
        const icalData = this.generateIcal(eventData, date);

        const blob = new Blob([icalData], {type: 'text/calendar'});
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${eventData.slug}-${date.date}.ics`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }

    generateIcal(eventData, date) {
        const dateStr = date.date.replace(/-/g, '');
        const contentStripped = eventData.content.replace(/<[^>]*>/g, '').substring(0, 200);
        const eventUrl = this.getEventUrl(eventData.id);

        let startTime = '000000';
        let endTime = '000000';

        if (date.time) {
            const timeStr = date.time.trim();

            if (timeStr.includes('-')) {
                const [start, end] = timeStr.split('-').map(t => t.trim());
                startTime = this.parseTimeToIcal(start);
                endTime = this.parseTimeToIcal(end);
            } else {
                startTime = this.parseTimeToIcal(timeStr);
                endTime = this.addOneHour(startTime);
            }
        }

        return `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Veranstaltungsprogramm//DE
CALSCALE:GREGORIAN
METHOD:PUBLISH
BEGIN:VEVENT
UID:${eventData.id}-${date.date}@${window.location.hostname}
DTSTAMP:${this.getCurrentTimestamp()}
DTSTART:${dateStr}T${startTime}
DTEND:${dateStr}T${endTime}
SUMMARY:${eventData.title}
DESCRIPTION:${contentStripped}
LOCATION:${eventData.organizer || ''}
URL:${eventUrl}
STATUS:CONFIRMED
END:VEVENT
END:VCALENDAR`;
    }

    parseTimeToIcal(timeStr) {
        timeStr = timeStr.trim();

        const timeMatch = timeStr.match(/(\d{1,2})[:.h]?(\d{2})?/);

        if (!timeMatch) {
            return '000000';
        }

        let hours = parseInt(timeMatch[1], 10);
        let minutes = parseInt(timeMatch[2] || '0', 10);

        if (/pm/i.test(timeStr) && hours < 12) {
            hours += 12;
        } else if (/am/i.test(timeStr) && hours === 12) {
            hours = 0;
        }

        const hoursStr = String(hours).padStart(2, '0');
        const minutesStr = String(minutes).padStart(2, '0');

        return `${hoursStr}${minutesStr}00`;
    }

    addOneHour(icalTime) {
        const hours = parseInt(icalTime.substring(0, 2), 10);
        const minutes = icalTime.substring(2, 4);

        let newHours = hours + 1;
        if (newHours >= 24) {
            newHours = 23;
        }

        return `${String(newHours).padStart(2, '0')}${minutes}00`;
    }

    getCurrentTimestamp() {
        const now = new Date();
        const year = now.getUTCFullYear();
        const month = String(now.getUTCMonth() + 1).padStart(2, '0');
        const day = String(now.getUTCDate()).padStart(2, '0');
        const hours = String(now.getUTCHours()).padStart(2, '0');
        const minutes = String(now.getUTCMinutes()).padStart(2, '0');
        const seconds = String(now.getUTCSeconds()).padStart(2, '0');

        return `${year}${month}${day}T${hours}${minutes}${seconds}Z`;
    }

    async copyEventLink(eventData) {
        const eventUrl = this.getEventUrl(eventData.id);

        if (navigator.clipboard && navigator.clipboard.writeText) {
            try {
                await navigator.clipboard.writeText(eventUrl);
                this.showShareFeedback('Link wurde in die Zwischenablage kopiert');
                return;
            } catch (err) {
                console.warn('Clipboard API failed, trying legacy method:', err);
            }
        }

        try {
            const textArea = document.createElement('textarea');
            textArea.value = eventUrl;
            textArea.style.position = 'fixed';
            textArea.style.left = '-999999px';
            textArea.style.top = '-999999px';
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();

            const successful = document.execCommand('copy');
            document.body.removeChild(textArea);

            if (successful) {
                this.showShareFeedback('Link wurde in die Zwischenablage kopiert');
            } else {
                throw new Error('execCommand copy failed');
            }
        } catch (err) {
            console.error('All copy methods failed:', err);
            this.showShareFeedback('Link konnte nicht kopiert werden. Bitte manuell kopieren: ' + eventUrl, true);
        }
    }

    async shareEvent(eventData) {
        const contentStripped = eventData.content.replace(/<[^>]*>/g, '').substring(0, 200);
        const eventUrl = this.getEventUrl(eventData.id);

        if (navigator.share) {
            try {
                await navigator.share({
                    title: eventData.title,
                    text: contentStripped,
                    url: eventUrl
                });
                console.log('Successfully shared via Web Share API');
                return;
            } catch (err) {
                if (err.name === 'AbortError') {
                    console.log('Share cancelled by user');
                    return;
                }
                console.warn('Web Share API failed, trying fallback:', err);
            }
        }

        if (navigator.clipboard && navigator.clipboard.writeText) {
            try {
                await navigator.clipboard.writeText(eventUrl);
                this.showShareFeedback('Link wurde in die Zwischenablage kopiert');
                console.log('Successfully copied via Clipboard API');
                return;
            } catch (err) {
                console.warn('Clipboard API failed, trying legacy method:', err);
            }
        }

        try {
            const textArea = document.createElement('textarea');
            textArea.value = eventUrl;
            textArea.style.position = 'fixed';
            textArea.style.left = '-999999px';
            textArea.style.top = '-999999px';
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();

            const successful = document.execCommand('copy');
            document.body.removeChild(textArea);

            if (successful) {
                this.showShareFeedback('Link wurde in die Zwischenablage kopiert');
                console.log('Successfully copied via execCommand');
            } else {
                throw new Error('execCommand copy failed');
            }
        } catch (err) {
            console.error('All copy methods failed:', err);
            this.showShareFeedback('Link konnte nicht kopiert werden. Bitte manuell kopieren: ' + eventUrl, true);
        }
    }

    showShareFeedback(message, isError = false) {
        const existingFeedback = document.querySelector('.share-feedback');
        if (existingFeedback) {
            existingFeedback.remove();
        }

        const feedback = document.createElement('div');
        feedback.className = 'share-feedback' + (isError ? ' share-feedback--error' : '');
        feedback.textContent = message;
        feedback.style.cssText = `
            position: fixed;
            bottom: var(--spacing-lg);
            left: 50%;
            transform: translateX(-50%);
            background-color: ${isError ? '#d32f2f' : '#2e7d32'};
            color: white;
            padding: var(--spacing-sm) var(--spacing-lg);
            border-radius: var(--border-radius);
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
            z-index: 99999;
            font-size: 0.9375rem;
            max-width: 90%;
            text-align: center;
            animation: slideUp 0.3s ease-out;
        `;

        document.body.appendChild(feedback);

        setTimeout(() => {
            feedback.style.animation = 'slideDown 0.3s ease-in';
            setTimeout(() => {
                if (feedback.parentNode) {
                    feedback.remove();
                }
            }, 300);
        }, 3000);
    }

    /**
     * Adjusts dropdown position to ensure it stays within viewport
     */
    adjustDropdownPosition(dropdown) {
        dropdown.classList.remove('align-right');

        setTimeout(() => {
            const rect = dropdown.getBoundingClientRect();
            const viewportWidth = window.innerWidth;
            const rightEdge = rect.right;

            if (rightEdge > viewportWidth) {
                dropdown.classList.add('align-right');
            }
        }, 0);
    }
}

document.addEventListener('DOMContentLoaded', () => {
    new VeranstaltungsProgramm();
});